import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Box, Button, Card, IconButton, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import uuid from 'uuid';
import { booleanYesOrNo, milkCategory, payZone, state, status } from './ReactHookProducerFormSelects';
import { getAllFederalOrders, getUserRegion, toTitleCase } from '../../../utils';
import { SearchableSelect } from '../../custom-widgets';
import { useFetchCustomFields } from '../../data/generic-queries';

const AndOrButtonSx = {
    height: 32,
    '& .MuiToggleButton-root.Mui-selected': {
        backgroundColor: '#0ea5e9',
        color: 'white',
        '&:hover': {
            backgroundColor: '#0284c7',
        },
    },
};

const DynamicUserSelection = ({ name }) => {
    const { watch, setValue } = useFormContext();
    const region = getUserRegion();
    const { data: availableCustomFields, isLoading: isLoadingAvailableCustomFields } = useFetchCustomFields({
        queryParams: { where: { role: 'producer' } },
        type: 'user',
        enabled: true,
        refetchOnMount: 'always',
    });

    const currentFilter = watch(name) || null;

    const [filter, setFilter] = useState({
        groups: [],
    });

    const baseAttributes = [
        { value: 'state', label: 'State', options: state },
        { value: 'federal_order', label: 'Federal Order', options: getAllFederalOrders() },
        { value: 'status', label: 'Status', options: status },
        { value: 'pay_group', label: 'Pay Group', options: payZone(region) },
        { value: 'milk_category', label: 'Milk Category', options: milkCategory },
        { value: 'self_haul', label: 'Self Haul', options: booleanYesOrNo },
    ];

    let dynamicAttributes = [];
    if (!isLoadingAvailableCustomFields && Array.isArray(availableCustomFields)) {
        const booleanCustomFields = availableCustomFields
            .filter((field) => field.data_type === 'boolean')
            .map((field) => ({
                value: field._id,
                label: toTitleCase(field.name, '_'),
                options: booleanYesOrNo,
            }));

        const customSelectFields = availableCustomFields
            .filter((field) => field.data_type === 'select' && Array.isArray(field.options) && field.options.length > 0)
            .map((field) => ({
                value: field._id,
                label: toTitleCase(field.name, '_'),
                options: field.options.map((option) => ({ name: option, value: option })),
            }));

        dynamicAttributes = [...booleanCustomFields, ...customSelectFields];
    }

    // merge and dedup
    const attributes = useMemo(() => [...baseAttributes, ...(dynamicAttributes ?? [])].filter((attr, index, self) => index === self.findIndex((a) => a.value === attr.value)), [dynamicAttributes]);

    const operators = [
        { value: '=', label: 'Is' },
        { value: '!=', label: 'Is Not' },
    ];

    const toggleRootOperator = (_event, newOperator) => {
        if (newOperator == null) return;

        const oldOperator = filter?.root_operator || 'AND';

        if (newOperator !== oldOperator) {
            setFilter((prevFilter) => {
                const newFilter = { ...prevFilter, root_operator: newOperator };
                updateFilterQuery(newFilter.groups, newOperator);
                return newFilter;
            });
        }
    };

    const addGroup = () => {
        setFilter((prevFilter) => {
            const newGroup = {
                id: uuid.v4(),
                operator: 'OR',
                rules: [{ id: uuid.v4(), attribute: '', operator: '=', value: '' }],
            };
            const newFilter = {
                ...prevFilter,
                root_operator: prevFilter.root_operator || 'AND',
                groups: [...prevFilter.groups, newGroup],
            };
            updateFilterQuery(newFilter.groups, newFilter.root_operator);
            return newFilter;
        });
    };

    const updateGroupRule = (groupId, ruleIndex, field, value) => {
        const newFilter = { ...filter };
        newFilter.groups = newFilter.groups.map((group) => {
            if (group.id === groupId) {
                const newRules = [...group.rules];
                const updatedRule = { ...newRules[ruleIndex], [field]: value };

                // Clear value when attribute changes to prevent invalid combinations
                if (field === 'attribute') {
                    updatedRule.value = '';
                }

                // For AND groups, disallow duplicate attributes by clearing them
                if (field === 'attribute' && group.operator === 'AND' && value) {
                    newRules.forEach((rule, index) => {
                        if (index !== ruleIndex && rule.attribute === value) {
                            newRules[index] = { ...rule, attribute: '', value: '' };
                        }
                    });
                }

                newRules[ruleIndex] = updatedRule;
                return { ...group, rules: newRules };
            }
            return group;
        });
        setFilter(newFilter);
        updateFilterQuery(newFilter.groups, newFilter.root_operator);
    };

    const addGroupRule = (groupId) => {
        const newFilter = { ...filter };
        newFilter.groups = newFilter.groups.map((group) => {
            if (group.id === groupId) {
                return { ...group, rules: [...group.rules, { id: uuid.v4(), attribute: '', operator: '=', value: '' }] };
            }
            return group;
        });
        setFilter(newFilter);
        updateFilterQuery(newFilter.groups);
    };

    const removeGroupRule = (groupId, ruleIndex) => {
        const newFilter = { ...filter };
        newFilter.groups = newFilter.groups.map((group) => {
            if (group.id === groupId) {
                const newRules = group.rules.filter((_, i) => i !== ruleIndex);
                return { ...group, rules: newRules };
            }
            return group;
        });
        setFilter(newFilter);
        updateFilterQuery(newFilter.groups);
    };

    const updateGroupOperator = (groupId, operator) => {
        const newFilter = { ...filter };
        newFilter.groups = newFilter.groups.map((group) => {
            if (group.id === groupId) {
                let updatedGroup = { ...group, operator };
                if (operator === 'AND') {
                    const seen = new Set();
                    updatedGroup = {
                        ...updatedGroup,
                        rules: updatedGroup.rules
                            .map((r) => {
                                if (!r.attribute) return r;
                                if (seen.has(r.attribute)) return { ...r, attribute: '', value: '' };
                                seen.add(r.attribute);
                                return r;
                            })
                            .filter((r) => r.attribute !== ''),
                    };
                }
                return updatedGroup;
            }
            return group;
        });
        setFilter(newFilter);
        updateFilterQuery(newFilter.groups, newFilter.root_operator);
    };

    const removeGroup = (groupId) => {
        const newFilter = { ...filter };
        newFilter.groups = newFilter.groups.filter((group) => group.id !== groupId);
        setFilter(newFilter);
        updateFilterQuery(newFilter.groups);
    };

    const updateFilterQuery = (currentGroups, rootOperator = null) => {
        const filterData = {
            root_operator: rootOperator || filter?.root_operator || 'AND',
            groups: currentGroups.filter((group) => group.rules.some((rule) => rule.attribute && rule.value)),
        };
        setValue(name, filterData);
    };

    // Get available attributes for a group (filtering out already selected ones for AND groups)
    const getAvailableAttributes = (groupId, currentRuleIndex) => {
        const group = filter.groups.find((g) => g.id === groupId);
        if (!group || group.operator === 'OR') {
            // For OR groups, all attributes are available
            return attributes.map((attr) => ({ name: attr.label, value: attr.value }));
        }

        // For AND groups, filter out already selected attributes
        const usedAttributes = group.rules.map((rule, index) => (index !== currentRuleIndex ? rule.attribute : null)).filter((attr) => attr && attr !== '');

        return attributes.filter((attr) => !usedAttributes.includes(attr.value)).map((attr) => ({ name: attr.label, value: attr.value }));
    };

    const getAttributeOptions = (attributeValue) => {
        if (!attributeValue || attributeValue === '' || !Array.isArray(attributes)) {
            return [];
        }
        const attribute = attributes.find((attr) => attr.value === attributeValue);
        if (!attribute || !Array.isArray(attribute.options)) {
            return [];
        }

        return attribute.options.map((option) => {
            if (typeof option === 'string') {
                return { name: option, value: option };
            }
            return option;
        });
    };

    useEffect(() => {
        if (currentFilter && currentFilter.groups && Array.isArray(currentFilter.groups) && currentFilter.groups.length > 0) {
            setFilter({
                root_operator: currentFilter.root_operator || 'AND',
                groups: currentFilter.groups,
            });
        } else if (currentFilter && (!currentFilter.groups || !Array.isArray(currentFilter.groups))) {
            setFilter({
                root_operator: currentFilter.root_operator || 'AND',
                groups: [{ id: uuid.v4(), operator: 'AND', rules: [{ id: uuid.v4(), attribute: '', operator: '=', value: '' }] }],
            });
        }
    }, []);

    return (
        <Box>
            <Box sx={{ mb: 1, mt: 2 }}>
                <Typography variant="h6" component="h3" sx={{ mb: 2, color: 'text.primary', fontWeight: 500 }}>
                    Dynamic User Selection
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Build dynamic filters using groups with AND/OR operators to automatically include users based on criteria
                </Typography>
            </Box>

            <Typography variant="subtitle1" gutterBottom>
                Filter Groups
            </Typography>

            <Stack spacing={2}>
                {(filter.groups || []).map((group, index) => (
                    <Stack key={group.id} direction="column" spacing={2} alignItems="center">
                        {index > 0 && (
                            <Box>
                                <ToggleButtonGroup value={filter?.root_operator || 'AND'} exclusive onChange={toggleRootOperator} size="small" sx={AndOrButtonSx}>
                                    <ToggleButton value="AND">AND</ToggleButton>
                                    <ToggleButton value="OR">OR</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        )}
                        <Card sx={{ p: 2, width: '100%' }} variant="outlined">
                            <Stack spacing={2}>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ justifyContent: 'space-between' }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Typography variant="subtitle2">Group {filter.groups.indexOf(group) + 1}</Typography>
                                        {group?.rules?.length > 1 && (
                                            <ToggleButtonGroup
                                                value={group.operator}
                                                exclusive
                                                onChange={(event, newOperator) => {
                                                    if (newOperator !== null) {
                                                        updateGroupOperator(group.id, newOperator);
                                                    }
                                                }}
                                                size="small"
                                                sx={AndOrButtonSx}
                                            >
                                                <ToggleButton value="AND">AND</ToggleButton>
                                                <ToggleButton value="OR">OR</ToggleButton>
                                            </ToggleButtonGroup>
                                        )}
                                    </Stack>

                                    <IconButton onClick={() => removeGroup(group.id)} color="error" size="small">
                                        <CloseIcon />
                                    </IconButton>
                                </Stack>

                                <Stack>
                                    {(group.rules || []).map((rule, ruleIndex) => (
                                        <Stack key={rule.id} direction="row" spacing={2} alignItems="center">
                                            <Box sx={{ flex: 1 }}>
                                                <SearchableSelect
                                                    label="Attribute"
                                                    options={getAvailableAttributes(group.id, ruleIndex)}
                                                    onChange={(event, selectedValue) => {
                                                        updateGroupRule(group.id, ruleIndex, 'attribute', selectedValue?.value ?? '');
                                                    }}
                                                    value={getAvailableAttributes(group.id, ruleIndex).find((attr) => attr.value === rule.attribute) || null}
                                                    variant="outlined"
                                                    hideCheckbox
                                                    closeOnSelect
                                                />
                                            </Box>

                                            <Box sx={{ flex: '0 0 auto', minWidth: 150 }}>
                                                <SearchableSelect
                                                    label="Operator"
                                                    options={operators.map((op) => ({ name: op.label, value: op.value }))}
                                                    onChange={(event, selectedValue) => {
                                                        updateGroupRule(group.id, ruleIndex, 'operator', selectedValue?.value ?? '=');
                                                    }}
                                                    value={operators.map((op) => ({ name: op.label, value: op.value })).find((op) => op.value === rule.operator) || null}
                                                    variant="outlined"
                                                    hideCheckbox
                                                    closeOnSelect
                                                />
                                            </Box>

                                            <Box sx={{ flex: 1 }}>
                                                <SearchableSelect
                                                    label="Value"
                                                    options={getAttributeOptions(rule.attribute)}
                                                    onChange={(event, selectedValue) => {
                                                        updateGroupRule(group.id, ruleIndex, 'value', selectedValue?.value ?? '');
                                                    }}
                                                    value={getAttributeOptions(rule.attribute).find((option) => option.value === rule.value) || null}
                                                    variant="outlined"
                                                    disabled={!rule.attribute}
                                                    hideCheckbox
                                                    closeOnSelect
                                                />
                                            </Box>

                                            <IconButton onClick={() => removeGroupRule(group.id, ruleIndex)} disabled={(group.rules || []).length === 1} color="error" size="small" sx={{ flex: '0 0 auto' }}>
                                                <CloseIcon />
                                            </IconButton>
                                        </Stack>
                                    ))}
                                </Stack>

                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Button onClick={() => addGroupRule(group.id)} startIcon={<AddIcon />} variant="outlined" size="small">
                                        Rule to Group
                                    </Button>
                                </Box>
                            </Stack>
                        </Card>
                    </Stack>
                ))}
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button onClick={addGroup} startIcon={<AddIcon />} variant="outlined" sx={{ borderStyle: 'dashed' }}>
                    Add Group
                </Button>
            </Box>
        </Box>
    );
};

export default DynamicUserSelection;
